import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import { updatePersonalData } from "../../../api/mutation";
import { Policy } from "../../../api/policy";
import HorizontalRadioInput from "../../../components/InputFields/HorizontalRadioInput";
import { V23, V26, V27, V48, V58 } from "../../../components/InputFields/sanitizer/inputSanitizers";
import { SelectionListInput, SelectOption } from "../../../components/InputFields/SelectionListInput";
import SwitchInput from "../../../components/InputFields/SwitchInput";
import TextInput from "../../../components/InputFields/TextInput";
import { V5, V10, V11 } from "../../../components/InputFields/validator/validators";
import { salutations, useOfName } from "../../../Enums/enumValues";
import { useDidMount } from "../../../hooks/useDidMount";
import { Enum, UseOfName } from "../../../interfaces/enum";
import { ModelState } from "../../../interfaces/modelState";
import { force } from "../../../typeguards/force";
import { MutationLayout } from "../MutationLayout";
import { personalDataMutationSitecoreLabels } from "./PersonalDataMutation.labels";
import { initialState, personalDataMutationReducer } from "./PersonalDataMutation.state";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
  policyNr: string;
  policy: Policy;
}

export const PersonalDataMutation = ({ fields, policyNr, policy }: Props) => {
  const history = useHistory();

  const salutationOptions = salutations() as Enum[];
  const useOfNameOptions = (useOfName() as Enum[])
    .filter((useOfNameOption) => useOfNameOption.value !== "OwnName")
    .map((useOfNameOption) => {
      return {
        key: useOfNameOption.value,
        value: useOfNameOption.label,
      };
    }) as SelectOption<UseOfName>[];

  const [{ data, loading, modelState }, dispatch] = useReducer(personalDataMutationReducer, initialState);

  // create a copy of the object, because the fields try to mutate it
  const copyOfModelState = { ...modelState };

  const handleInsuredOldName = (oldName: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { oldName } });
  };

  const handleInsuredSalutation = (salutation: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { salutation } });
  };

  const handleInsuredInitials = (initials: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { initials } });
  };

  const handleInsuredFirstName = (firstName: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { firstName } });
  };

  const handleInsuredInserts = (inserts: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { inserts } });
  };

  const handleInsuredLastName = (lastName: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { lastName } });
  };

  const handleInsuredUsePartnerName = (usePartnerName: boolean) => {
    if (usePartnerName) {
      dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { usePartnerName, useOfName: "PartnerName" } });
    } else {
      dispatch({
        type: "UPDATE_PERSONAL_DATA_MUTATION_DATA",
        payload: {
          usePartnerName,
          partnerInserts: undefined,
          partnerLastName: undefined,
          useOfName: undefined,
        },
      });
    }
  };

  const handleInsuredPartnerInserts = (partnerInserts: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { partnerInserts } });
  };

  const handleInsuredPartnerLastName = (partnerLastName: string) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { partnerLastName } });
  };

  const handleInsuredUseOfName = (value: UseOfName) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_DATA", payload: { useOfName: value } });
  };

  const setModelState = (newModelState: ModelState) => {
    dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_ERROR", payload: newModelState });
  };

  const handlePersonalDataMutationClick = async () => {
    try {
      dispatch({ type: "UPDATE_PERSONAL_DATA_MUTATION_FETCH_START" });
      const request = {
        salutation: force(data.salutation),
        initials: force(data.initials),
        firstName: force(data.firstName),
        inserts: data.inserts,
        lastName: force(data.lastName),
        usePartnerName: force(data.usePartnerName),
        ...(data.usePartnerName && {
          partnerLastName: data.partnerLastName,
          useOfName: data.useOfName,
          partnerInserts: data.partnerInserts,
        }),
        oldName: force(data.oldName),
      };
      const response = await updatePersonalData({ ...request, policyNumber: policyNr });

      if (response.modelState) {
        dispatch({
          type: "UPDATE_PERSONAL_DATA_MUTATION_FETCH_ERROR",
          payload: response.modelState,
        });
      } else {
        // remove the query parameters
        window.history.replaceState(null, "", window.location.pathname);
        history.push(fields.ThankYouPage.url);
      }
    } catch {
      dispatch({
        type: "UPDATE_PERSONAL_DATA_MUTATION_FETCH_ERROR",
        payload: { global: ["Er is een onverwachte fout opgetreden. Probeer het opnieuw."] },
      });
    }
  };

  const canSubmitForm = () => {
    const isValidPartnerNameIfExist = !data.usePartnerName || (data.partnerLastName && data.useOfName);
    const firstNameLastNameExist = data.firstName && data.lastName;
    const salutationInitialsExist = data.salutation && data.initials;

    return Boolean(data.oldName && salutationInitialsExist && firstNameLastNameExist && isValidPartnerNameIfExist);
  };

  const policyPersons = [...(policy.insureds?.insured ?? []), ...(policy.policyHolders?.policyHolder ?? [])]
    .map((insured) => {
      const { naturalPersonSummary } = insured;

      const name = [naturalPersonSummary.initials, naturalPersonSummary.prefixName, naturalPersonSummary.surName]
        .filter(Boolean)
        .join(" ");

      return name;
    })
    .filter((value, index, self) => self.indexOf(value) === index)
    .map((personName) => ({ key: personName, value: personName }));

  useDidMount(() => {
    if (policyPersons.length !== 0) {
      handleInsuredOldName(policyPersons[0].key);
    }
  });

  return (
    <MutationLayout
      canSubmitForm={canSubmitForm()}
      handleSubmit={handlePersonalDataMutationClick}
      loading={loading}
      modelState={copyOfModelState}
      title="Persoonsgegevens wijzigen"
      CardContent={
        <>
          {policyPersons.length > 0 && (
            <SelectionListInput
              fieldData={personalDataMutationSitecoreLabels}
              options={policyPersons}
              value={data.oldName}
              name="OldName"
              propertyName="model.OldName"
              fieldName="OldName"
              onChange={handleInsuredOldName}
              hideDefaultSelection
            />
          )}

          <HorizontalRadioInput
            fieldData={personalDataMutationSitecoreLabels}
            name="Salutation"
            setModelState={setModelState}
            selectedValue={data.salutation}
            options={salutationOptions}
            modelState={copyOfModelState}
            fieldName="Salutation"
            propertyName="model.Salutation"
            onChange={handleInsuredSalutation}
          />
          <div className="form-row form-row--flex">
            <TextInput
              fieldData={personalDataMutationSitecoreLabels}
              name="Initials"
              sanitize={[V23, V48, V58]}
              validators={[V5, V10(7)]}
              setModelState={setModelState}
              inputValue={data.initials}
              modelState={copyOfModelState}
              onBlur={handleInsuredInitials}
              fieldName="Initials"
              propertyName="model.Initials"
              widthClassName="short"
              excludeRow={true}
              required
            />
            <TextInput
              fieldData={personalDataMutationSitecoreLabels}
              name="FirstName"
              sanitize={[V27, V48, V58]}
              validators={[V5, V10(35), V11(2)]}
              setModelState={setModelState}
              inputValue={data.firstName}
              modelState={copyOfModelState}
              onBlur={handleInsuredFirstName}
              fieldName="FirstName"
              propertyName="model.FirstName"
              widthClassName="medium"
              excludeRow={true}
              required
            />
          </div>
          <div className="form-row form-row--flex">
            <TextInput
              fieldData={personalDataMutationSitecoreLabels}
              name="Inserts"
              sanitize={[V48]}
              validators={[V10(10)]}
              setModelState={setModelState}
              inputValue={data.inserts}
              modelState={copyOfModelState}
              onBlur={handleInsuredInserts}
              fieldName="Inserts"
              propertyName="model.Inserts"
              widthClassName="short"
              excludeRow={true}
            />
            <TextInput
              fieldData={personalDataMutationSitecoreLabels}
              name="LastName"
              sanitize={[V26, V48, V58]}
              validators={[V5, V10(35)]}
              setModelState={setModelState}
              inputValue={data.lastName}
              modelState={copyOfModelState}
              onBlur={handleInsuredLastName}
              fieldName="LastName"
              propertyName="model.LastName"
              widthClassName="medium"
              excludeRow={true}
              required
            />
          </div>

          <SwitchInput
            fieldData={personalDataMutationSitecoreLabels}
            value={data.usePartnerName}
            name="UsePartnerName"
            fieldName="UsePartnerName"
            onChange={handleInsuredUsePartnerName}
          />
          {data.usePartnerName && (
            <>
              <div className="form-row form-row--flex">
                <TextInput
                  fieldData={personalDataMutationSitecoreLabels}
                  name="PartnerInserts"
                  sanitize={[V48]}
                  validators={[V10(10)]}
                  setModelState={setModelState}
                  inputValue={data.partnerInserts}
                  modelState={copyOfModelState}
                  fieldName="PartnerInsert"
                  propertyName="model.PartnerInserts"
                  onBlur={handleInsuredPartnerInserts}
                  widthClassName="short"
                  excludeRow={true}
                />
                <TextInput
                  fieldData={personalDataMutationSitecoreLabels}
                  name="PartnerLastName"
                  sanitize={[V26, V48, V58]}
                  validators={[V5, V10(35)]}
                  setModelState={setModelState}
                  inputValue={data.partnerLastName}
                  modelState={copyOfModelState}
                  fieldName="PartnerLastName"
                  propertyName="model.PartnerLastName"
                  onBlur={handleInsuredPartnerLastName}
                  widthClassName="medium"
                  excludeRow={true}
                  required
                />
              </div>
              <SelectionListInput
                id="partner_selection"
                fieldData={personalDataMutationSitecoreLabels}
                options={useOfNameOptions}
                value={data.useOfName}
                name="UseOfName"
                propertyName="model.UseOfName"
                fieldName="UseOfName"
                onChange={handleInsuredUseOfName}
                hideDefaultSelection={true}
              />
            </>
          )}
        </>
      }
      InfoContent={
        <>
          <h6>Verwerkingstermijn</h6>
          <p>
            Wijzigingen zijn niet direct zichtbaar in het DELAportaal De verwerkingstermijn bedraagt maximaal 2
            werkdagen. Er wordt <strong>geen</strong> nieuwe polis afgegeven.
          </p>

          <h6>Wijziging andere persoonsgegevens</h6>
          <p>
            Wijziging van andere persoonsgegevens dan de aanhef of naam kun je hier niet doorgeven. Annuleer deze
            wijziging en kies onder ‘Polis wijzigen’ voor ‘Overig’. Geef hier de wijziging van de andere
            persoonsgegevens door.
          </p>
        </>
      }
    />
  );
};
